import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { NextPage } from "next";
import {
  Box,
  Flex,
  Text,
  Image,
  Center,
  Divider,
  Show,
} from "@chakra-ui/react";

// Components
import { PageContext, Footer, Header, useStaderTVL, BreadCrum } from "mlp-sdk";

// Providers
import { PageProvider } from "providers/PageProvider";

// Types
import {
  AngelsCardType,
  StrapiImageCard,
  StrapiSectionDetailType,
} from "mlp-sdk/types/strapi";

// Utils
import { prepareNavData } from "mlp-sdk/utils/common";
import { trackEvent } from "mlp-sdk/utils/firebase";

import { LP_PARTNERS_PAGE } from "mlp-sdk/constants/firebase";
import navData from "@constants/navData.json";
import partnersPageData from "@constants/partnersPage.json";
import PageHead from "@components/PageHead/PageHead";

interface AnglesType {
  section_details?: StrapiSectionDetailType;
  angels: AngelsCardType[];
}

interface FundedChainsType {
  section_details?: StrapiSectionDetailType;
  fund_chains: StrapiImageCard[];
}

const FUNDS = "Funds";
const BLOCKCHAINS = "Blockchains";
const ANGELS = "Angels";

const NAVITEMS = [FUNDS, BLOCKCHAINS, ANGELS];

const PartnersPage = () => {
  const { page } = useContext(PageContext);
  const pageData = page.data.pageData;

  const [activeMenuKey, setActiveMenuKey] = useState(FUNDS);
  const [isBannerScrolled, setIsBannerScrolled] = useState(false);

  const { tvl } = useStaderTVL();

  const { navData, networks } = useMemo(
    () => prepareNavData(page.data.navData, tvl),
    [page.data.navData, tvl]
  );

  let angelsData: AnglesType = {
    angels: [],
  };

  let fundedChainsData: FundedChainsType = {
    fund_chains: [],
  };

  let fundsData = [];
  if (pageData) {
    const { Angels, FundChains, Funds } = pageData;
    angelsData = Angels;
    fundedChainsData = FundChains;
    fundsData = Funds?.funds;
  }

  const onSubHeaderChange = useCallback((key: string) => {
    const section = document.querySelector(`#${key}`) as HTMLElement | null;
    trackEvent(LP_PARTNERS_PAGE, {
      partner_tab: key,
    });
    section &&
      section.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "start",
      });
  }, []);

  const renderMenuItem = useCallback(
    (key: string) => {
      return (
        <Text
          fontWeight="600"
          fontSize="p16"
          cursor="pointer"
          onClick={() => onSubHeaderChange(key)}
          color={activeMenuKey === key ? "textPrimary" : "textTertiary"}
        >
          {key}
        </Text>
      );
    },
    [activeMenuKey, onSubHeaderChange]
  );

  useEffect(() => {
    const mainElements = NAVITEMS.map((item: string) =>
      document.getElementById(item)
    );

    const observer = new IntersectionObserver(
      (entries) => {
        for (let entry of entries) {
          if (entry.isIntersecting) {
            setActiveMenuKey(entry.target.id);
          }
        }
      },
      {
        threshold: window?.innerWidth > 992 ? 0.9 : 0.4,
      }
    );

    mainElements.forEach((element) => element && observer.observe(element));

    // cleanup function
    return () => {
      mainElements.forEach((element) => element && observer.unobserve(element));
    };
  }, []);

  useEffect(() => {
    const heroSection = document.getElementById("partnersHeroSection");
    const observer = new IntersectionObserver(
      (entries) => {
        for (let entry of entries) {
          if (entry.isIntersecting) {
            setIsBannerScrolled(true);
          } else {
            setIsBannerScrolled(false);
          }
        }
      },
      {
        threshold: 1,
      }
    );
    heroSection && observer.observe(heroSection);

    // cleanup function
    return () => {
      heroSection && observer.unobserve(heroSection);
    };
  }, []);

  return (
    <>
      {/* <Head>
        <title>Stader</title>
        <meta name="description" content="Stader" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.staderlabs.com/partners/" />
      </Head> */}
      <PageHead page="partners" />
      <Header
        data={navData}
        networks={networks}
        bgColor={isBannerScrolled ? "transparent" : undefined}
        tvl={tvl}
      />

      {/* // TODO Temporary solution by margin (as position changed to sticky) height should be dynamic */}

      <Box
        id="partnersHeroSection"
        className="partnerBanner"
        mt={{ base: 0, lg: "-86px" }}
      >
        <Show below="lg">
          <BreadCrum />
        </Show>
        <Center
          mt={{ base: "66px", lg: "34px" }}
          h="calc(100% - 72px)"
          w="100%"
          justifyContent={{ base: "flex-end", md: "center" }}
          flexDir="column"
          pb="52px"
        >
          <Show above="lg">
            <Box w="100%" maxW="1192px">
              <BreadCrum ml={0} />
            </Box>
          </Show>
          <Text fontWeight="700" fontSize={{ base: "32px", md: "64px" }}>
            Our Partners
          </Text>
        </Center>
      </Box>
      <Show below="lg">
        <Box bg="#FEEAE2" position="sticky" top="57px" width="100%">
          <Box>
            <Flex w="100%" gap="1rem" justifyContent="center">
              {NAVITEMS.map((item: string) => (
                <Center
                  key={item}
                  cursor="pointer"
                  flexDir="column"
                  p="16px 12px 0"
                  minW="fit-content"
                  onClick={() => {
                    onSubHeaderChange(item);
                  }}
                >
                  <Text
                    textStyle={activeMenuKey === item ? "h3" : "p16"}
                    fontSize="16px"
                    lineHeight="inherit"
                    color={activeMenuKey === item ? "black" : "textSecondary"}
                    mb="1rem"
                  >
                    {item}
                  </Text>
                  <Divider
                    w="120%"
                    border="2px solid"
                    borderColor={
                      activeMenuKey === item ? "#07C166" : "transparent"
                    }
                    borderTopRadius={4}
                  />
                </Center>
              ))}
            </Flex>
          </Box>
          <Divider border="1px solid" borderColor="#AEDFCA" />
        </Box>
      </Show>
      <Flex
        w={{ base: "100%" }}
        maxW="1192px"
        p={{ base: "64px 15.5px", lg: "80px 0px" }}
        justifyContent={{ base: "center", lg: "space-between" }}
        textAlign={{ base: "center", lg: "left" }}
      >
        <Show above="lg">
          <Box>
            <Flex gap="20px" flexDir="column" position="sticky" top="80px">
              {renderMenuItem(FUNDS)}
              {renderMenuItem(BLOCKCHAINS)}
              {renderMenuItem(ANGELS)}
            </Flex>
          </Box>
        </Show>

        <Flex
          flexDir="column"
          gap={{ base: "120px", lg: "64px" }}
          maxW="787px"
          mb="80px"
        >
          <Box id={FUNDS} scrollMargin="120px">
            <Text fontWeight="700" fontSize="24px" lineHeight="28px">
              Funds
            </Text>
            <Flex
              mt="24px"
              flexWrap="wrap"
              gap={{ base: "16px", lg: "8px" }}
              justifyContent={{ base: "center", lg: "flex-start" }}
            >
              {fundsData.map((data: StrapiImageCard) => (
                <Center
                  w={{ base: "99px", md: "190px" }}
                  h={{ base: "34px", md: "120px" }}
                  borderRadius={{ base: "4px", md: "6px" }}
                  bg="#E6F8EF"
                  key={data.img_key}
                >
                  <Image
                    src={data.img_key}
                    w={{ base: "99px", md: "190px" }}
                    h={{ base: "34px", md: "120px" }}
                    alt={`${data.img_key}-icon`}
                  />
                </Center>
              ))}
            </Flex>
          </Box>

          <Divider
            w="100%"
            color="divider"
            border="1px solid"
            display={{ base: "none", lg: "inherit" }}
          />

          <Box id={BLOCKCHAINS} scrollMargin="120px">
            <Text fontWeight="700" fontSize="24px" lineHeight="28px">
              {fundedChainsData?.section_details?.title}
            </Text>
            <Flex
              mt="24px"
              flexWrap="wrap"
              gap={{ base: "16px", lg: "8px" }}
              justifyContent={{ base: "center", lg: "flex-start" }}
            >
              {fundedChainsData.fund_chains.map((data: StrapiImageCard) => (
                <Center
                  w={{ base: "99px", md: "190px" }}
                  h={{ base: "34px", md: "120px" }}
                  borderRadius={{ base: "4px", md: "6px" }}
                  bg="#E6F8EF"
                  key={data.img_key}
                >
                  <Image
                    src={data.img_key}
                    w={{ base: "99px", md: "190px" }}
                    h={{ base: "34px", md: "120px" }}
                    alt={`${data.img_key}-icon`}
                  />
                </Center>
              ))}
            </Flex>
          </Box>

          <Divider
            w="100%"
            color="divider"
            border="1px solid"
            display={{ base: "none", xl: "inherit" }}
          />

          <Box id={ANGELS} scrollMargin="120px">
            <Text fontWeight="700" fontSize="24px" lineHeight="28px">
              {angelsData?.section_details?.title}
            </Text>
            <Flex
              mt="24px"
              flexWrap="wrap"
              gap={{ base: "16px", lg: "8px" }}
              justifyContent={{ base: "center", lg: "flex-start" }}
            >
              {angelsData.angels.map((data: AngelsCardType) => (
                <Center
                  w={{ base: "328px", md: "257px" }}
                  h={{ base: "80px", md: "120px" }}
                  borderRadius="6px"
                  bg="#E6F8EF"
                  key={data.name}
                  flexDir="column"
                >
                  <Text
                    textStyle={{ base: "p14", lg: "p16" }}
                    fontWeight="600 !important"
                  >
                    {data.name}
                  </Text>
                  <Text textStyle={{ base: "p10", lg: "p12" }}>
                    {data.designation}
                  </Text>
                </Center>
              ))}
            </Flex>
          </Box>
        </Flex>
      </Flex>
      <Footer data={navData} />
    </>
  );
};

const PartnersDetailPage: NextPage = ({ page }: any) => {
  return (
    <PageProvider page={page}>
      <PartnersPage />
    </PageProvider>
  );
};

export async function getStaticProps() {
  return {
    props: {
      page: {
        data: {
          navData,
          pageData: partnersPageData,
        },
        error: { navError: null, pageError: null },
      },
    },
  };
}

export default PartnersDetailPage;
